import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAddEmailDomain } from "@/hooks/use-email-auth";

interface AddDomainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddDomainDialog({ open, onOpenChange }: AddDomainDialogProps) {
  const [domain, setDomain] = useState("");
  const [dkimSelector, setDkimSelector] = useState("default");
  const mutation = useAddEmailDomain();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!domain.trim()) return;

    await mutation.mutateAsync({ domain: domain.trim(), dkim_selector: dkimSelector.trim() || "default" });
    setDomain("");
    setDkimSelector("default");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Email Domain</DialogTitle>
          <DialogDescription>
            Register a domain to monitor its SPF, DKIM, and DMARC configuration.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain</Label>
            <Input
              id="domain"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dkim-selector">DKIM Selector</Label>
            <Input
              id="dkim-selector"
              placeholder="default"
              value={dkimSelector}
              onChange={(e) => setDkimSelector(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Common selectors: default, google, selector1 (Microsoft), k1 (Mailchimp)
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Adding..." : "Add Domain"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
